import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseApi } from '../services/supabase';
import { 
  Tag, 
  ArrowLeft, 
  Save, 
  Palette,
  Globe,
  TrendingUp,
  ShoppingCart,
  Brain,
  Cloud,
  Database
} from 'lucide-react';
import styled from 'styled-components';

const AddCategoryContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
`;

const Form = styled.form`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  max-width: 600px;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const IconOption = styled.div<{ selected: boolean }>`
  width: 60px;
  height: 60px;
  border: 2px solid ${props => props.selected ? '#667eea' : '#e2e8f0'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#f0f4ff' : 'white'};
  
  &:hover {
    border-color: #667eea;
    background: #f0f4ff;
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ColorInput = styled.input`
  width: 60px;
  height: 40px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 8px;
  }
`;

const ColorPreview = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color || '#667eea'};
  border: 2px solid #e2e8f0;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.variant === 'secondary' ? '#e2e8f0' : 'transparent'};
  background: ${props => props.variant === 'secondary' ? 'white' : '#667eea'};
  color: ${props => props.variant === 'secondary' ? '#374151' : 'white'};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const iconOptions = [
  { value: 'Globe', icon: <Globe size={24} />, label: 'Globe' },
  { value: 'Smartphone', icon: '📱', label: 'Mobile' },
  { value: 'Palette', icon: <Palette size={24} />, label: 'Design' },
  { value: 'TrendingUp', icon: <TrendingUp size={24} />, label: 'Marketing' },
  { value: 'ShoppingCart', icon: <ShoppingCart size={24} />, label: 'E-commerce' },
  { value: 'Brain', icon: <Brain size={24} />, label: 'AI/ML' },
  { value: 'Cloud', icon: <Cloud size={24} />, label: 'Cloud' },
  { value: 'Database', icon: <Database size={24} />, label: 'Database' },
];

const AddCategory: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Globe',
    color: '#667eea'
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlugChange = (name: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, name, slug }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const success = await supabaseApi.createCategory(formData);
      if (success) {
        navigate('/categories');
      }
    } catch (error) {
      console.error('Error creating category:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AddCategoryContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem onClick={() => navigate('/')}>
            <Tag size={20} />
            Dashboard
          </NavItem>
          <NavItem onClick={() => navigate('/projects')}>
            <Tag size={20} />
            Projects
          </NavItem>
          <NavItem onClick={() => navigate('/categories')}>
            <Tag size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageHeader>
            <BackButton onClick={() => navigate('/categories')}>
              <ArrowLeft size={20} />
            </BackButton>
            <PageTitle>Add New Category</PageTitle>
          </PageHeader>

          <Form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Category Information</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Category Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    required
                    placeholder="Enter category name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Slug *</Label>
                  <Input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    required
                    placeholder="category-slug"
                  />
                  <small style={{ color: '#64748b' }}>
                    This will be used in URLs and API calls
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe what this category is about"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Icon</Label>
                  <Select
                    value={formData.icon}
                    onChange={(e) => handleInputChange('icon', e.target.value)}
                  >
                    {iconOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  
                  <IconGrid>
                    {iconOptions.map((option) => (
                      <IconOption
                        key={option.value}
                        selected={formData.icon === option.value}
                        onClick={() => handleInputChange('icon', option.value)}
                        title={option.label}
                      >
                        {option.icon}
                      </IconOption>
                    ))}
                  </IconGrid>
                </FormGroup>

                <FormGroup>
                  <Label>Color</Label>
                  <ColorPicker>
                    <ColorInput
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                    />
                    <ColorPreview color={formData.color} />
                    <span style={{ color: '#64748b', fontSize: '14px' }}>
                      {formData.color}
                    </span>
                  </ColorPicker>
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/categories')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : (
                  <>
                    <Save size={16} style={{ marginRight: '8px' }} />
                    Create Category
                  </>
                )}
              </Button>
            </FormActions>
          </Form>
        </Content>
      </MainContent>
    </AddCategoryContainer>
  );
};

export default AddCategory;
